import { supabase } from '../lib/supabase';
/**
 * Service for managing clinicians and viewing their patients
 * Patients are associated with clinicians through device usage, not explicit assignments
 */
export class ClinicianService {
    /**
     * Get all clinicians with their patient counts
     * @returns Array of clinicians with patient count
     * @throws Error if user is not an admin or database error occurs
     */
    static async getAllClinicians() {
        const { data, error } = await supabase.rpc('get_all_clinicians');
        if (error) {
            console.error('Error fetching clinicians:', error);
            throw new Error(error.message || 'Unable to fetch clinicians');
        }
        return data || [];
    }
    /**
     * Get all patients in the system
     * @returns Array of all patients
     * @throws Error if user is not an admin or database error occurs
     */
    static async getAllPatients() {
        const { data, error } = await supabase.rpc('get_all_patients');
        if (error) {
            console.error('Error fetching patients:', error);
            throw new Error(error.message || 'Unable to fetch patients');
        }
        return data || [];
    }
    /**
     * Get patients who have used devices assigned to a specific clinician
     * @param clinicianId - The ID of the clinician
     * @param studyId - Optional study ID to filter patients by study
     * @returns Array of patients with device usage information
     * @throws Error if user is not an admin or database error occurs
     */
    static async getClinicianPatients(clinicianId, studyId) {
        const { data, error } = await supabase.rpc('get_clinician_patients', {
            p_clinician_id: clinicianId,
            p_study_id: studyId || null
        });
        if (error) {
            console.error('Error fetching clinician patients:', error);
            throw new Error(error.message || 'Unable to fetch clinician patients');
        }
        return data || [];
    }
    /**
     * Get a specific clinician's profile
     * @param clinicianId - The ID of the clinician
     * @returns The clinician's profile
     * @throws Error if clinician not found or database error occurs
     */
    static async getClinicianProfile(clinicianId) {
        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', clinicianId)
            .single();
        if (error) {
            console.error('Error fetching clinician profile:', error);
            // If it's a "not found" error, return null
            if (error.code === 'PGRST116') {
                return null;
            }
            // For other errors, throw
            throw new Error(error.message || 'Unable to fetch clinician profile');
        }
        return data;
    }
}
